import styled from "@emotion/styled";
import Mustache from "mustache";
import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";

import { DeviceDesktop, DeviceMobile } from "@livechat/design-system-icons";
import { Icon, SegmentedControl, SpacingToken, Text } from "@livechat/design-system-react-components";

import Block from "components/layout/Block";
import Flex from "components/layout/Flex";
import generatePreviewTemplate from "routes/settings/email-template/generatePreviewTemplate";
import { IAppearance } from "store/entities/application-settings/interfaces";
import { selectExampleTicket, selectPoweredByFooterCode } from "store/entities/application-settings/selectors";
import { selectHasPoweredByFooter } from "store/entities/license";
import { withVar } from "themes/helpers";

import { PreviewType } from "../constants";

import { TestEmailTemplate } from "./components/TestEmailTemplate";
import { IframeBox, PreviewHeader } from "./components/styled";
import { ContentType } from "./constants";

interface IPreviewTemplateProps {
  contentType: ContentType;
  templates: Record<ContentType, string>;
  isBannerVisible: boolean;
  appearance: IAppearance;
  hasChanged: boolean;
  sendTest: (emails: string[]) => void;
  disabled: boolean;
  isVisualBuilderPreview: boolean;
}
// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line import/namespace
Mustache.templateCache = undefined;

const PreviewTemplate: React.FC<IPreviewTemplateProps> = ({
  contentType,
  templates,
  appearance,
  isVisualBuilderPreview,
  hasChanged,
  disabled,
  sendTest,
  isBannerVisible,
}) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [content, setContent] = useState(
    contentType === ContentType.HTML ? templates[ContentType.HTML] : templates[ContentType.PlainText]
  );
  const [ready, setIsReady] = useState(false);
  const [previewWidth, setPreviewWidth] = useState("100%");
  const exampleTicket = useSelector(selectExampleTicket) as Record<string, string>;
  const hasPoweredByFooter = useSelector(selectHasPoweredByFooter);

  const poweredByFooter = useSelector(selectPoweredByFooterCode);
  const newContent = generatePreviewTemplate(
    contentType,
    isVisualBuilderPreview,
    appearance,
    templates,
    exampleTicket,
    poweredByFooter,
    hasPoweredByFooter
  );

  const handlePreviewWidthChange = (id: string): void => {
    switch (id as PreviewType) {
      case PreviewType.Desktop:
        setPreviewWidth("100%");
        break;
      case PreviewType.Mobile:
        setPreviewWidth("320px");
        break;
      default:
        setPreviewWidth("100%");
    }
  };

  useEffect(() => {
    setIsReady(false);
    setContent(newContent);
    setIsReady(true);
  }, [newContent]);

  useEffect(() => {
    if (iframeRef.current) {
      const iframe = iframeRef.current;
      iframe.addEventListener("load", function insertContentOnLoad() {
        if (iframe.contentDocument) {
          Array.from(iframe.contentDocument.querySelectorAll("a")).forEach(link => {
            (link as HTMLElement).setAttribute("target", "_blank noreferrer noopener");
          });
        }
      });
      if (typeof iframe.srcdoc !== "undefined" && ready && iframe.contentWindow) {
        iframe.contentWindow.document.open();
        iframe.contentWindow.document.write(content);
        iframe.contentWindow.document.close();
      }
    }
  }, [content]);

  switch (contentType) {
    case ContentType.HTML:
      return (
        <Block paddingV={!isVisualBuilderPreview ? "2em" : undefined}>
          <PreviewHeader isVisualBuilderPreview={isVisualBuilderPreview}>
            <PreviewContainer>
              {isVisualBuilderPreview ? (
                <Flex alignItems="center">
                  <Block marginRight="10px">
                    <Text bold>Preview</Text>
                  </Block>
                </Flex>
              ) : (
                <div />
              )}
              <CenteredSegmentedControlWrapper>
                <SegmentedControl
                  buttons={[
                    { id: "desktop", label: "", icon: <Icon source={DeviceDesktop} /> },
                    { id: "mobile", label: "", icon: <Icon source={DeviceMobile} /> },
                  ]}
                  initialId="desktop"
                  onButtonClick={handlePreviewWidthChange}
                />
              </CenteredSegmentedControlWrapper>
              {isVisualBuilderPreview ? (
                <TestEmailTemplate hasChanged={hasChanged} sendTest={sendTest} disabled={disabled} />
              ) : (
                <div />
              )}
            </PreviewContainer>
          </PreviewHeader>
          <IframeBox
            width={previewWidth}
            isBannerVisible={isBannerVisible}
            isVisualBuilderPreview={isVisualBuilderPreview}
            isSaveBarVisible={hasChanged}
          >
            <Flex>
              <iframe
                ref={iframeRef}
                sandbox="allow-same-origin"
                referrerPolicy="no-referrer"
                frameBorder="0"
                allowFullScreen={false}
                scrolling="auto"
                data-testid="template-preview"
              />
            </Flex>
          </IframeBox>
        </Block>
      );
    default:
      return (
        <Block paddingV="2em">
          <pre>{content}</pre>
        </Block>
      );
  }
};

const CenteredSegmentedControlWrapper = styled.div`
  position: absolute;
  display: flex;
  justify-content: center;
  left: 50%;
  transform: translateX(-50%);
`;

const PreviewContainer = styled.div`
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;
  min-height: ${withVar(SpacingToken.Spacing8)};
`;

export default PreviewTemplate;
